// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — administration protocol library: read + pharmacy-admin writes (E-02: components never call api.*).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listCareRxAdminProtocols } from '../../api/client'
import {
  createProtocol, editProtocol, getProtocolUsage, getSlotImpact, retireProtocol, type ProtocolInput, type ProtocolSlot,
} from '../../api/carerxProtocols'

export const protocolKeys = {
  library: ['carerx', 'admin-protocols'] as const,
  usage: (id: string) => ['carerx', 'admin-protocols', 'usage', id] as const,
  slotImpact: (drug: string, slot: ProtocolSlot) => ['carerx', 'admin-protocols', 'slot-impact', drug, slot] as const,
}

/** Enrolled patients of a drug with no protocol in an (empty) slot — they keep none if one is assigned. */
export function useSlotImpact(drugReferenceId: string | null, slot: ProtocolSlot, enabled: boolean) {
  return useQuery({
    queryKey: protocolKeys.slotImpact(drugReferenceId ?? '', slot),
    queryFn: () => getSlotImpact(drugReferenceId!, slot),
    enabled: enabled && !!drugReferenceId,
    staleTime: 30_000,
  })
}

/** The pharmacy's current library (one current version per code) + the component catalog. */
export function useProtocolLibrary() {
  return useQuery({ queryKey: protocolKeys.library, queryFn: listCareRxAdminProtocols, staleTime: 60_000 })
}

/** Which drugs use a protocol (the edit / retire warnings). Always fetched fresh when asked for. */
export function useProtocolUsage(id: string | null) {
  return useQuery({
    queryKey: protocolKeys.usage(id ?? ''),
    queryFn: () => getProtocolUsage(id!),
    enabled: !!id,
    staleTime: 0,
  })
}

function useInvalidateLibrary() {
  const qc = useQueryClient()
  // Prefix: the library list and every usage query.
  return () => qc.invalidateQueries({ queryKey: protocolKeys.library })
}

export function useCreateProtocol() {
  const done = useInvalidateLibrary()
  return useMutation({
    mutationFn: (v: { body: ProtocolInput; assignDrugReferenceId?: string; copiedFrom?: string }) =>
      createProtocol(v.body, v.assignDrugReferenceId, v.copiedFrom),
    onSuccess: done,
  })
}

export function useEditProtocol() {
  const done = useInvalidateLibrary()
  return useMutation({
    mutationFn: (v: { id: string; expectedVersion: number; body: ProtocolInput }) => editProtocol(v.id, v.expectedVersion, v.body),
    onSuccess: done,
  })
}

export function useRetireProtocol() {
  const done = useInvalidateLibrary()
  return useMutation({
    mutationFn: (v: { id: string; unassign: boolean }) => retireProtocol(v.id, v.unassign),
    onSuccess: done,
  })
}
